import { type ContractInvoicingDto, formatMoney, PAYMENT_METHODS, type PaymentDto, type PaymentItemStatus, type PaymentMethod, todayInWarsaw } from '@bqcrm/shared';
import { Button, Checkbox, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { notifyError, notifyOk, notifyWarn } from '../notify';
import { PaymentAmountField, paymentEffect, usePaymentImpact } from './PaymentAmount';

/** Pozycja harmonogramu, do której można przypisać wpłatę (PaymentItemDto lub wiersz należności). */
export interface PayableItem {
  id: string;
  name: string;
  amountGr: number;
  paidGr: number;
  status: PaymentItemStatus;
}

/**
 * „Zarejestruj wpłatę” — wspólne okno dla karty umowy i modułu Finanse.
 * Wpłata trafia zawsze do umowy głównej (`contractId`); pozycja może należeć także do podpisanego aneksu.
 */
export function PaymentDialog({
  opened,
  onClose,
  contractId,
  items,
  initialItemId,
  onSaved,
}: {
  opened: boolean;
  onClose: () => void;
  contractId: string;
  items: PayableItem[];
  initialItemId?: string | null;
  onSaved: () => void;
}) {
  return (
    <Modal opened={opened} onClose={onClose} title="Rejestracja wpłaty">
      {opened && <PaymentForm contractId={contractId} items={items} initialItemId={initialItemId ?? null} onSaved={onSaved} onClose={onClose} />}
    </Modal>
  );
}

function PaymentForm({ contractId, items, initialItemId, onSaved, onClose }: { contractId: string; items: PayableItem[]; initialItemId: string | null; onSaved: () => void; onClose: () => void }) {
  const open = items.filter((s) => s.status === 'oczekuje' || s.status === 'czesciowo');
  const initial = open.find((s) => s.id === initialItemId) ?? null;
  const [itemId, setItemId] = useState<string | null>(initial?.id ?? null);
  // QA qa10311123 3.1: kwota pusta na start — pełną pozostałą wpisuje przycisk „Cała kwota”
  const [amountGr, setAmountGr] = useState<number | null>(null);
  const item = open.find((s) => s.id === itemId) ?? null;
  const remainingGr = item ? item.amountGr - item.paidGr : null;
  const impact = usePaymentImpact(contractId);
  const effect = paymentEffect(amountGr, impact.data);
  const [paidAt, setPaidAt] = useState(todayInWarsaw());
  const [method, setMethod] = useState<PaymentMethod>('przelew');
  const [reference, setReference] = useState('');
  const qc = useQueryClient();
  // faktury: tylko gdy firma ma integrację i użytkownik może wystawiać (inaczej zapytanie zwróci błąd — pole się nie pokaże)
  const inv = useQuery({ queryKey: ['contract-invoicing', contractId], queryFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoicing`), retry: false });
  // płatność końcowa albo płatność aneksu (P1: rozlicza ją faktura VAT do aneksu) — bez faktury zaliczkowej
  const toFinal = !!itemId && (itemId === inv.data?.final.paymentItemId || !!inv.data?.schedule.find((s) => s.paymentItemId === itemId)?.annexContractId);
  const canAdvance = !!inv.data?.connection && inv.data.can.issue && !toFinal && !inv.data.invoices.some((i) => ['koncowa', 'vat'].includes(i.kind) && i.status !== 'anulowana' && !i.annexNumber);
  const [advance, setAdvance] = useState(true);
  const save = useMutation({
    mutationFn: async () => {
      const list = await api<PaymentDto[]>(`/contracts/${contractId}/payments`, { method: 'POST', body: { paymentItemId: itemId, amountGr, paidAt, method, reference } });
      if (!(canAdvance && advance)) return { invoice: null as string | null, skipped: false };
      // nowa wpłata = najnowsza z tą kwotą i pozycją (lista zwrócona po zapisie)
      const mine = list.filter((p) => p.amountGr === amountGr && p.paymentItemId === itemId && !p.reversalOf).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      if (!mine) return { invoice: null, skipped: true };
      try {
        const st = await api<ContractInvoicingDto>(`/contracts/${contractId}/invoices/for-payment`, { method: 'POST', body: { paymentId: mine.id, paymentDays: 0 } });
        qc.setQueryData(['contract-invoicing', contractId], st);
        return { invoice: st.payments.find((p) => p.paymentId === mine.id)?.invoice?.number ?? 'wystawiona', skipped: false };
      } catch (e) {
        // wpłata zapisana — faktura do ponowienia w zakładce Faktury
        notifyError(e);
        return { invoice: null, skipped: true };
      }
    },
    onSuccess: (r) => {
      if (r.skipped) notifyWarn('Wpłata zapisana, ale faktury zaliczkowej nie wystawiono — wystaw ją w umowie (zakładka „Harmonogram i wpłaty” albo „Faktury”).');
      else notifyOk(r.invoice ? `Zarejestrowano wpłatę i wystawiono fakturę zaliczkową ${r.invoice}` : 'Zarejestrowano wpłatę');
      void qc.invalidateQueries({ queryKey: ['contract-invoicing', contractId] });
      void qc.invalidateQueries({ queryKey: ['payment-impact', contractId] });
      onSaved();
    },
    onError: notifyError,
  });
  return (
    <Stack>
      <Select
        label="Pozycja harmonogramu"
        data={open.map((s) => ({ value: s.id, label: `${s.name} — pozostało ${formatMoney(s.amountGr - s.paidGr)}` }))}
        value={itemId}
        onChange={(v) => {
          const it = open.find((s) => s.id === v) ?? null;
          setItemId(it?.id ?? null);
        }}
        clearable
        placeholder="bez przypisania"
      />
      <PaymentAmountField amountGr={amountGr} onChangeGr={setAmountGr} remainingGr={remainingGr} what="pozycja" impact={impact.data} />
      <TextInput label="Data wpłaty" type="date" max={todayInWarsaw()} value={paidAt} onChange={(e) => setPaidAt(e.currentTarget.value)} />
      <Select label="Sposób" data={Object.entries(PAYMENT_METHODS).map(([value, label]) => ({ value, label }))} value={method} onChange={(v) => v && setMethod(v as PaymentMethod)} allowDeselect={false} />
      <TextInput label="Tytuł przelewu lub nr dowodu wpłaty" placeholder="opcjonalnie" value={reference} onChange={(e) => setReference(e.currentTarget.value)} />
      {canAdvance && (
        <Checkbox
          checked={advance}
          onChange={(e) => setAdvance(e.currentTarget.checked)}
          label="Wystaw fakturę zaliczkową na tę wpłatę"
          description={
            (inv.data?.connection?.mode === 'test' ? 'Konto testowe Fakturowni. ' : 'Faktura trafi do Fakturowni (i KSeF, jeśli włączony) — nie da się jej usunąć, tylko skorygować. ') +
            (!inv.data?.email.autoSend || inv.data.email.mode === 'brak'
              ? 'Nie zostanie wysłana automatycznie.'
              : inv.data.email.to
                ? `Zostanie wysłana e-mailem do ${inv.data.email.to}.`
                : 'Klient nie ma adresu e-mail — nie zostanie wysłana.')
          }
        />
      )}
      {toFinal && inv.data?.connection && (
        <Text size="xs" c="dimmed">
          Wpłata na płatność końcową — rozliczy ją faktura końcowa.
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button loading={save.isPending} disabled={!amountGr || amountGr <= 0 || (remainingGr !== null && amountGr > remainingGr)} color={effect === 'sprawa' ? 'orange' : undefined} onClick={() => save.mutate()}>
          {effect === 'sprawa' ? 'Zarejestruj i zamknij sprawę' : 'Zarejestruj wpłatę'}
        </Button>
      </Group>
    </Stack>
  );
}
